import {
  Archive,
  ArrowRightLeft,
  Ban,
  CalendarCog,
  CircleCheck,
  Eye,
  EyeClosed,
  EyeOff,
  KeyRound,
  Lightbulb,
  ListPlus,
  LogIn,
  LogOut,
  type LucideIcon,
  Mail,
  MessageSquare,
  OctagonMinus,
  Pencil,
  Repeat,
  RotateCcw,
  ScrollText,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Timer,
  ToggleLeft,
  ToggleRight,
  Trash2,
  UserCog,
  UserPen,
  UserPlus,
  UsersRound,
  UserX,
  Wrench,
} from "lucide-react";

import type { nl } from "@/lib/i18n/locales/nl";

/** An action the activity log stores, as the texts name them. */
type KnownAction = keyof typeof nl.activity.actions;

/** A line icon per action, in the Tools page's style; an action not listed gets the log's own icon. */
const ACTION_ICONS: Record<KnownAction, LucideIcon> = {
  login: LogIn,
  signup: UserPlus,
  profile_update: UserPen,
  password_change: KeyRound,
  admin_update_user: UserCog,
  admin_delete_user: UserX,
  admin_revoke_sessions: LogOut,
  admin_invite: Mail,
  admin_first_account: ShieldCheck,
  admin_mcp_enable_tool: ToggleRight,
  admin_mcp_disable_tool: ToggleLeft,
  admin_mcp_enable_tool_group: ToggleRight,
  admin_mcp_disable_tool_group: ToggleLeft,
  admin_mcp_block_user: Ban,
  admin_mcp_unblock_user: CircleCheck,
  admin_mcp_team_create: UsersRound,
  admin_mcp_team_update: Pencil,
  admin_mcp_team_delete: Trash2,
  admin_mcp_team_members: UserPlus,
  admin_mcp_person_override: SlidersHorizontal,
  admin_mcp_issue_acknowledge: Wrench,
  admin_mcp_issue_resolve: CircleCheck,
  admin_mcp_issue_ignore: EyeOff,
  admin_mcp_issue_reopen: RotateCcw,
  roadmap_item_create: Lightbulb,
  roadmap_item_update: Pencil,
  roadmap_item_status: ArrowRightLeft,
  roadmap_item_delete: Trash2,
  roadmap_item_effort: Timer,
  roadmap_month_budget: CalendarCog,
  roadmap_item_blocker: OctagonMinus,
  roadmap_item_comment: MessageSquare,
  roadmap_gap_promote: Lightbulb,
  roadmap_gap_close: CircleCheck,
  roadmap_triage_propose: Send,
  roadmap_triage_accept: ListPlus,
  roadmap_triage_close: Archive,
  view_as_start: Eye,
  view_as_switch: Repeat,
  view_as_stop: EyeClosed,
};

interface ActionIconProps {
  /** The action as stored, such as `admin_revoke_sessions`. */
  action: string;
  className?: string;
}

/** The action's line icon, decorative: the row says what happened. */
export function ActionIcon({ action, className }: ActionIconProps) {
  const Icon = Object.hasOwn(ACTION_ICONS, action)
    ? ACTION_ICONS[action as KnownAction]
    : ScrollText;
  return <Icon aria-hidden="true" className={className} strokeWidth={1.75} />;
}
